import * as metaData from '../metaData';
import { toFiniteNumber } from './toNumber';
function generateFrameImageId(baseImageId, frameNumber) {
    const framePattern = /\/frames\/\d+/;
    const queryFramePattern = /([?&])frame=\d+/;
    if (framePattern.test(baseImageId)) {
        return baseImageId.replace(framePattern, `/frames/${frameNumber}`);
    }
    if (queryFramePattern.test(baseImageId)) {
        return baseImageId.replace(queryFramePattern, (_, separator) => `${separator}frame=${frameNumber}`);
    }
    const separator = baseImageId.includes('?') ? '&' : '?';
    return `${baseImageId}${separator}frame=${frameNumber}`;
}
function handleMultiframe4D(imageIds) {
    if (!imageIds || imageIds.length === 0) {
        return null;
    }
    const baseImageId = imageIds[0];
    const instance = metaData.get('instance', baseImageId);
    if (!instance) {
        return null;
    }
    const numberOfFrames = instance.NumberOfFrames;
    if (!numberOfFrames || numberOfFrames <= 1) {
        return null;
    }
    const timeSlotVector = instance.TimeSlotVector;
    if (!timeSlotVector || !Array.isArray(timeSlotVector)) {
        return null;
    }
    const sliceVector = instance.SliceVector;
    const numberOfSlices = instance.NumberOfSlices;
    if (timeSlotVector.length !== numberOfFrames) {
        console.warn('TimeSlotVector length does not match NumberOfFrames:', timeSlotVector.length, 'vs', numberOfFrames);
        return null;
    }
    if (sliceVector) {
        if (!Array.isArray(sliceVector)) {
            console.warn('SliceVector exists but is not an array. Expected length:', numberOfFrames);
            return null;
        }
        if (sliceVector.length !== numberOfFrames ||
            sliceVector.some((val) => val === undefined)) {
            console.warn('SliceVector exists but has invalid length or undefined entries. Expected length:', numberOfFrames, 'Actual length:', sliceVector.length);
            return null;
        }
    }
    const timeSlotGroups = new Map();
    for (let frameIndex = 0; frameIndex < numberOfFrames; frameIndex++) {
        const timeSlot = timeSlotVector[frameIndex];
        const sliceIndex = sliceVector?.[frameIndex] ?? frameIndex;
        if (!timeSlotGroups.has(timeSlot)) {
            timeSlotGroups.set(timeSlot, []);
        }
        timeSlotGroups.get(timeSlot).push({ frameIndex, sliceIndex });
    }
    const sortedTimeSlots = Array.from(timeSlotGroups.keys()).sort((a, b) => a - b);
    const imageIdGroups = sortedTimeSlots.map((timeSlot) => {
        const frames = timeSlotGroups.get(timeSlot);
        frames.sort((a, b) => a.sliceIndex - b.sliceIndex);
        return frames.map((frame) => generateFrameImageId(baseImageId, frame.frameIndex + 1));
    });
    const expectedSlicesPerTimeSlot = numberOfSlices || imageIdGroups[0]?.length;
    const allGroupsHaveSameLength = imageIdGroups.every((group) => group.length === expectedSlicesPerTimeSlot);
    if (!allGroupsHaveSameLength) {
        console.warn('Multiframe 4D split resulted in uneven time slot groups. Expected', expectedSlicesPerTimeSlot, 'slices per time slot.');
    }
    return {
        imageIdGroups,
        splittingTag: 'TimeSlotVector',
    };
}
function handleCardiac4D(imageIds) {
    if (!imageIds || imageIds.length === 0) {
        return null;
    }
    const cardiacNumberOfImages = getFiniteValue(imageIds[0], 'CardiacNumberOfImages');
    if (cardiacNumberOfImages === undefined) {
        return null;
    }
    const stacks = new Map();
    for (const imageId of imageIds) {
        const stackId = metaData.get('StackID', imageId);
        const inStackPositionNumber = getFiniteValue(imageId, 'InStackPositionNumber');
        const triggerTime = getFiniteValue(imageId, 'TriggerTime');
        if (stackId === undefined ||
            inStackPositionNumber === undefined ||
            triggerTime === undefined) {
            return null;
        }
        const stackKey = String(stackId);
        if (!stacks.has(stackKey)) {
            stacks.set(stackKey, new Map());
        }
        const positions = stacks.get(stackKey);
        if (!positions.has(inStackPositionNumber)) {
            positions.set(inStackPositionNumber, []);
        }
        positions.get(inStackPositionNumber).push({ imageId, triggerTime });
    }
    const sortedStackIds = Array.from(stacks.keys()).sort((a, b) => Number(a) - Number(b));
    if (sortedStackIds.length === 0) {
        return null;
    }
    const preparedStacks = [];
    let timeCount;
    for (const stackId of sortedStackIds) {
        const positions = stacks.get(stackId);
        const sortedPositions = Array.from(positions.keys()).sort((a, b) => a - b);
        for (const position of sortedPositions) {
            const frames = positions.get(position);
            frames.sort((a, b) => a.triggerTime - b.triggerTime);
            if (timeCount === undefined) {
                timeCount = frames.length;
            }
            else if (frames.length !== timeCount) {
                return null;
            }
        }
        preparedStacks.push({
            stackId,
            positions: sortedPositions,
            framesByPosition: positions,
        });
    }
    if (!timeCount) {
        return null;
    }
    const imageIdGroups = [];
    for (let timeIndex = 0; timeIndex < timeCount; timeIndex++) {
        const group = [];
        for (const stack of preparedStacks) {
            for (const position of stack.positions) {
                const frames = stack.framesByPosition.get(position);
                group.push(frames[timeIndex].imageId);
            }
        }
        imageIdGroups.push(group);
    }
    return {
        imageIdGroups,
        splittingTag: 'CardiacTriggerTime',
    };
}
const groupBy = (array, key) => {
    return array.reduce((rv, x) => {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
    }, {});
};
function getIPPGroups(imageIds) {
    const ippMetadata = imageIds.map((imageId) => {
        const { imagePositionPatient } = metaData.get('imagePlaneModule', imageId) || {};
        return { imageId, imagePositionPatient };
    });
    if (!ippMetadata.every((item) => item.imagePositionPatient)) {
        return null;
    }
    const positionGroups = groupBy(ippMetadata, 'imagePositionPatient');
    const positions = Object.keys(positionGroups);
    const frame_count = positionGroups[positions[0]].length;
    if (frame_count === 1) {
        return null;
    }
    const frame_count_equal = positions.every((k) => positionGroups[k].length === frame_count);
    if (!frame_count_equal) {
        return null;
    }
    return positionGroups;
}
function test4DTag(IPPGroups, value_getter) {
    const frame_groups = {};
    let first_frame_value_set = [];
    const positions = Object.keys(IPPGroups);
    for (let i = 0; i < positions.length; i++) {
        const frame_value_set = new Set();
        const frames = IPPGroups[positions[i]];
        for (let j = 0; j < frames.length; j++) {
            const frame_value = value_getter(frames[j].imageId) || 0;
            frame_groups[frame_value] = frame_groups[frame_value] || [];
            frame_groups[frame_value].push({ imageId: frames[j].imageId });
            frame_value_set.add(frame_value);
            if (frame_value_set.size - 1 < j) {
                return undefined;
            }
        }
        if (i == 0) {
            first_frame_value_set = Array.from(frame_value_set);
        }
        else if (!setEquals(first_frame_value_set, frame_value_set)) {
            return undefined;
        }
    }
    return frame_groups;
}
function getTagValue(imageId, tag) {
    const value = metaData.get(tag, imageId);
    try {
        return parseFloat(value);
    }
    catch {
        return undefined;
    }
}
function getFiniteValue(imageId, tag) {
    return toFiniteNumber(getTagValue(imageId, tag));
}
function getPhilipsPrivateBValue(imageId) {
    const value = metaData.get('20011003', imageId);
    try {
        const { InlineBinary } = value;
        if (InlineBinary) {
            const value_bytes = atob(InlineBinary);
            const ary_buf = new ArrayBuffer(value_bytes.length);
            const dv = new DataView(ary_buf);
            for (let i = 0; i < value_bytes.length; i++) {
                dv.setUint8(i, value_bytes.charCodeAt(i));
            }
            return new Float32Array(ary_buf)[0];
        }
        return parseFloat(value);
    }
    catch {
        return undefined;
    }
}
function getSiemensPrivateBValue(imageId) {
    let value = metaData.get('0019100c', imageId) || metaData.get('0019100C', imageId);
    try {
        const { InlineBinary } = value;
        if (InlineBinary) {
            value = atob(InlineBinary);
        }
        return parseFloat(value);
    }
    catch {
        return undefined;
    }
}
function getGEPrivateBValue(imageId) {
    let value = metaData.get('00431039', imageId);
    try {
        const { InlineBinary } = value;
        if (InlineBinary) {
            value = atob(InlineBinary).split('//');
        }
        return parseFloat(value[0]) % 100000;
    }
    catch {
        return undefined;
    }
}
function setEquals(set_a, set_b) {
    if (set_a.length != set_b.size) {
        return false;
    }
    for (let i = 0; i < set_a.length; i++) {
        if (!set_b.has(set_a[i])) {
            return false;
        }
    }
    return true;
}
function getPetFrameReferenceTime(imageId) {
    const moduleInfo = metaData.get('petImageModule', imageId);
    return moduleInfo ? moduleInfo['frameReferenceTime'] : 0;
}
function splitImageIdsBy4DTags(imageIds) {
    const multiframeResult = handleMultiframe4D(imageIds);
    if (multiframeResult) {
        return multiframeResult;
    }
    const cardiacResult = handleCardiac4D(imageIds);
    if (cardiacResult) {
        return cardiacResult;
    }
    const positionGroups = getIPPGroups(imageIds);
    if (!positionGroups) {
        return { imageIdGroups: [imageIds], splittingTag: null };
    }
    const tags = [
        'TemporalPositionIdentifier',
        'DiffusionBValue',
        'TriggerTime',
        'EchoTime',
        'EchoNumber',
        'PhilipsPrivateBValue',
        'SiemensPrivateBValue',
        'GEPrivateBValue',
        'PetFrameReferenceTime',
    ];
    const fncList2 = [
        (imageId) => getTagValue(imageId, tags[0]),
        (imageId) => getTagValue(imageId, tags[1]),
        (imageId) => getTagValue(imageId, tags[2]),
        (imageId) => getTagValue(imageId, tags[3]),
        (imageId) => getTagValue(imageId, tags[4]),
        getPhilipsPrivateBValue,
        getSiemensPrivateBValue,
        getGEPrivateBValue,
        getPetFrameReferenceTime,
    ];
    for (let i = 0; i < fncList2.length; i++) {
        const frame_groups = test4DTag(positionGroups, fncList2[i]);
        if (frame_groups) {
            const sortedKeys = Object.keys(frame_groups)
                .map(Number.parseFloat)
                .sort((a, b) => a - b);
            const imageIdGroups = sortedKeys.map((key) => frame_groups[key].map((item) => item.imageId));
            return { imageIdGroups, splittingTag: tags[i] };
        }
    }
    return { imageIdGroups: [imageIds], splittingTag: null };
}
export default splitImageIdsBy4DTags;
export { handleMultiframe4D, generateFrameImageId };
